import { StorageSerializers, useSessionStorage } from "@vueuse/core";
import { useFetch } from "~vue/api";
import { useProfileSources } from "~vue/composables/useProfileSources";
import { computed, ref } from "vue";

export interface ProfileSnapshotEntry {
    id: string;
    content: string;
}

export interface ProfileSnapshot {
    entries: ProfileSnapshotEntry[];
    sourceIds: string[];
}

interface SubtopicSavePayload {
    subtopic_id: string;
    content: string;
}

export const useProfileUpdate = (topicName: string) => {
    const savePayload = ref<SubtopicSavePayload | null>(null);
    const { execute: executeSave, error: saveError } = useFetch("/api/accounts/profile/topics/", { immediate: false }).post(savePayload).json();

    const saveSubtopic = async (id: string, content: string): Promise<boolean> => {
        savePayload.value = { subtopic_id: id, content };
        await executeSave();
        return !saveError.value;
    };

    const { handleDeleteSource, deleteSourceError } = useProfileSources();

    const snapshot = useSessionStorage<ProfileSnapshot | null>(`collab-profile-undo:${topicName}`, null, {
        serializer: StorageSerializers.object,
    });

    const isRestoring = ref(false);
    const restoreError = ref<string | null>(null);

    const takeSnapshot = (entries: ProfileSnapshotEntry[], sourceIds: string[]): void => {
        snapshot.value = { entries, sourceIds };
    };

    const restore = async (currentSourceIds: string[]): Promise<boolean> => {
        const previous = snapshot.value;
        if (!previous || isRestoring.value) {
            return false;
        }

        isRestoring.value = true;
        restoreError.value = null;
        try {
            for (const entry of previous.entries) {
                if (!(await saveSubtopic(entry.id, entry.content))) {
                    throw saveError.value;
                }
            }

            const previousSourceIds = new Set(previous.sourceIds);
            for (const sourceId of currentSourceIds.filter((id) => !previousSourceIds.has(id))) {
                await handleDeleteSource(sourceId);
                if (deleteSourceError.value) {
                    throw deleteSourceError.value;
                }
            }

            snapshot.value = null;
            return true;
        } catch {
            restoreError.value = "Failed to restore the previous profile. Please try again.";
            return false;
        } finally {
            isRestoring.value = false;
        }
    };

    return {
        saveSubtopic,
        canRestore: computed(() => snapshot.value !== null),
        isRestoring,
        restoreError,
        takeSnapshot,
        restore,
    };
};
